import { API_BASE_URL } from '@types';
import type {
  SessionGroup,
  SessionGroupListItem,
  SessionGroupStore,
} from '../types';

const buildUrl = (path: string) =>
  `${API_BASE_URL}/services/v1/session-groups${path}`;

const normalizeGroup = (group: SessionGroup): SessionGroup => ({
  ...group,
  sessionIds: group.sessionIds ?? [],
  sessionCount: group.sessionCount ?? group.sessionIds?.length ?? 0,
});

const normalizeListItem = (
  item: SessionGroupListItem
): SessionGroupListItem => ({
  ...item,
  sessionCount: item.sessionCount ?? 0,
});

export const sessionGroupStore: SessionGroupStore = {
  async listGroups() {
    const response = await fetch(buildUrl(''), {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Lista gruppi sessioni fallita: HTTP ${response.status} ${message}`
      );
    }

    const parsed = (await response.json()) as SessionGroupListItem[];
    return Array.isArray(parsed)
      ? parsed
          .map(normalizeListItem)
          .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
      : [];
  },

  async getGroup(id) {
    const response = await fetch(buildUrl(`/${encodeURIComponent(id)}`), {
      method: 'GET',
      credentials: 'include',
    });

    if (response.status === 404) return null;

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Recupero gruppo fallito: HTTP ${response.status} ${message}`
      );
    }

    const parsed = (await response.json()) as SessionGroup;
    return normalizeGroup(parsed);
  },

  async createGroup(payload) {
    const response = await fetch(buildUrl(''), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Creazione gruppo fallita: HTTP ${response.status} ${message}`
      );
    }

    const parsed = (await response.json()) as SessionGroup;
    return normalizeGroup(parsed);
  },

  async updateGroup(id, payload) {
    const response = await fetch(buildUrl(`/${encodeURIComponent(id)}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Aggiornamento gruppo fallito: HTTP ${response.status} ${message}`
      );
    }

    const parsed = (await response.json()) as SessionGroup;
    return normalizeGroup(parsed);
  },

  async deleteGroup(id) {
    const response = await fetch(buildUrl(`/${encodeURIComponent(id)}`), {
      method: 'DELETE',
      credentials: 'include',
    });

    if (response.status === 404) {
      throw new Error('Gruppo non trovato.');
    }

    if (!response.ok) {
      const message = await response.text();
      throw new Error(
        `Eliminazione gruppo fallita: HTTP ${response.status} ${message}`
      );
    }
  },
};
